import { useEffect } from "react";
import ProductListing from "./ProductListing";
import { columnTypes } from "src/components/UI/atoms/ATMTable/ATMTable";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/redux/store";
import {
  setIsOpenUploadExcelDialog,
  setPage,
  setRowsPerPage,
  setSearchValue,
  setIsTableLoading,
  setItems,
  setTotalItems,
} from "src/redux/slices/ProductSlice";
import ATMMenu from "src/components/UI/atoms/ATMMenu/ATMMenu";
import {
  useChangeStatusProductByIdMutation,
  useDeleteProductByIdMutation,
  useGetAllProductDataQuery,
  useLazyGetProductExcelDownloadQuery,
} from "src/services/ProductService";
import { showConfirmationDialog } from "src/utils/showConfirmationDialog";
import { showToast } from "src/utils/showToaster";
import { useNavigate } from "react-router-dom";
import UploadExcelWrapper from "../Upload/UploadExcelWrapper";
import { usePermission } from "src/hooks/usePermission";

type Props = {};

const ProductListingWrapper = (props: Props) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    items,
    totalItems,
    page,
    rowsPerPage,
    searchValue,
    isTableLoading,
    filterBy,
    isOpenUploadExcelDialog,
  } = useSelector((state: RootState) => state.product);

  const [deleteProduct] = useDeleteProductByIdMutation();
  const [triggerDownloadProductExcel] = useLazyGetProductExcelDownloadQuery();
  const handleDownloadProductExcel = async () => {
    try {
      const res: any = await triggerDownloadProductExcel("");

      if (res?.error) {
        showToast("error", res?.error?.data?.message);
        return;
      }

      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = "ProductList.xlsx"; // file name
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      showToast("error", "Download failed");
    }
  };
  const handleDownloadProductTemplateExcel = async () => {
    try {
      // 🔹 Default file URL
      const fileUrl =
        "https://your-academy.s3.ap-south-1.amazonaws.com/your-academy/image/image_1758601203940_20250923095003.xlsx";

      // 🔹 Fetch file as blob
      const response = await fetch(fileUrl);
      if (!response.ok) {
        showToast("error", "Failed to fetch file");
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      // 🔹 Create a hidden <a> and trigger download
      const a = document.createElement("a");
      a.href = url;
      a.download = "ProductList.xlsx"; // file name
      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      showToast("error", "Download failed");
    }
  };

  const [changeStatusProduct] = useChangeStatusProductByIdMutation();
  const { data, isLoading, isFetching } = useGetAllProductDataQuery({
    limit: rowsPerPage,
    searchValue: searchValue,
    params: ["productName"],
    page: page,
    filterBy: filterBy?.map((filter) => ({
      fieldName: filter.fieldName,
      value: filter.value.filter((v) => v !== ""),
    })),

    dateFilter: {},
    orderBy: "createdAt",
    orderByValue: -1,
    isPaginationRequired: true,
  });

  useEffect(() => {
    if (isLoading || isFetching) {
      dispatch(setIsTableLoading(true));
    } else {
      dispatch(setItems(data?.data || []));
      dispatch(setTotalItems(data?.totalItem || 0));
      dispatch(setIsTableLoading(false));
    }
  }, [data, isLoading, isFetching, dispatch]);

  const handleDeleteProduct = (row: string) => {
    showConfirmationDialog({
      title: "Hands Up",
      text: "Are you sure want to delete this Product Management ?",
      icon: "question",
      showCancelButton: true,
      next: (result) => {
        if (result?.isConfirmed) {
          deleteProduct(row).then((res: any) => {
            if (res.error) {
              showToast("error", res?.error?.data?.message);
            } else {
              showToast("success", res?.data?.message);
            }
          });
        }
      },
    });
  };

  const handleChangeStatusProduct = (row: string) => {
    showConfirmationDialog({
      title: "Hands Up",
      text: "Are you sure want to change status of this Advance Payment ?",
      icon: "question",
      showCancelButton: true,
      next: (result) => {
        if (result?.isConfirmed) {
          changeStatusProduct({ id: row }).then((res: any) => {
            if (res.error) {
              showToast("error", res?.error?.data?.message);
            } else {
              showToast("success", res?.data?.message);
            }
          });
        }
      },
    });
  };
  const moduleName = "PRODUCT";

  const canEdit = usePermission(moduleName, "UPDATE");
  const canDelete = usePermission(moduleName, "DELETE");
  const canActive = usePermission(moduleName, "ACTIVE/DEACTIVE");

  const columns: columnTypes[] = [
    {
      field: "productName",
      headerName: "Product Name",
      flex: "flex-[2_2_0%]",
      renderCell: (params) => (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <img
            src={params?.image?.[0]}
            alt="icon"
            width={70}
            height={70}
            style={{ borderRadius: "4px" }}
          />
          <span>{params?.productName}</span>
        </div>
      ),
    },
    {
      field: "modeName",
      headerName: "mode name",
      flex: "flex-[1.5_1.5_0%]",
    },
    {
      field: "varientCode",
      headerName: "Varient Code",
      flex: "flex-[1.5_1.5_0%]",
    },
    {
      field: "productMrp",
      headerName: "mrp",
      flex: "flex-[1.5_1.5_0%]",
    },
    {
      field: "productSellingPrice",
      headerName: "Selling Price",
      flex: "flex-[1.5_1.5_0%]",
    },

    {
      field: "action",
      headerName: "Actions",
      flex: "flex-[1_1_0%]",
      align: "end",
      extraClasses: "",
      renderCell: (row: any) => {
        const options = [];

        if (canEdit) {
          options.push({
            label: "Edit",
            onClick: () => {
              navigate(`/product/edit/${row._id}`);
            },
          });
        }

        if (canActive) {
          options.push({
            label: "Active/Deactive",
            onClick: () => {
              handleChangeStatusProduct(row?._id);
            },
          });
        }

        if (canDelete) {
          options.push({
            label: "Delete",
            onClick: () => {
              handleDeleteProduct(row?._id);
            },
          });
        }

        return <ATMMenu options={options} />;
      },
    },
  ];

  return (
    <>
      {" "}
      <div>
        <ProductListing
          columns={columns}
          productExcel={handleDownloadProductExcel}
          templateExcel={handleDownloadProductTemplateExcel}
          rows={items}
          paginationProps={{
            isTableLoading,
            totalItems,
            page,
            rowsPerPage,
            searchValue,
            setIsOpenUploadExcelDialog: (value) =>
              dispatch(setIsOpenUploadExcelDialog(value)),
            setPage: (newPage) => dispatch(setPage(newPage)),
            setRowsPerPage: (newRowsPerPage) =>
              dispatch(setRowsPerPage(newRowsPerPage)),
            setSearchValue: (newValue) => dispatch(setSearchValue(newValue)),
          }}
        />
      </div>
      {isOpenUploadExcelDialog && (
        <UploadExcelWrapper
          onClose={() => dispatch(setIsOpenUploadExcelDialog(false))}
        />
      )}
    </>
  );
};

export default ProductListingWrapper;
